import { Box, IInputProps, Input, Text, useToken } from 'native-base';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform } from 'react-native';

export type TextInputProps = IInputProps & {
  label?: string;
  children?: ReactNode;
};

export default function TextInput({
  label,
  placeholder,
  value,
  onFocus,
  onBlur,
  children,
  ...inputProps
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(!!value);
  const labelAnim = useRef(new Animated.Value(value ? 1 : 0)).current;
  const [gray300] = useToken('colors', ['gray.300']);

  useEffect(() => {
    const toValue = isFocused || !!value ? 1 : 0;
    Animated.timing(labelAnim, {
      toValue,
      duration: 200,
      easing: Easing.ease,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [labelAnim, isFocused, value]);

  const handleFocus = (e: any) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChangeText = (text: string) => {
    const newHasValue = text.length > 0;
    setHasValue(newHasValue);
    inputProps.onChangeText?.(text);
  };

  const labelStyle = {
    position: 'absolute' as const,
    left: 12,
    color: gray300,
    zIndex: 1,
    fontSize: isFocused || hasValue ? 10 : 14,
    transform: [
      {
        translateY: labelAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [16, 8],
        }),
      },
    ],
  };

  const { _focus, ...restInputProps } = inputProps;

  return (
    <Box position="relative" width="100%">
      <Animated.Text style={labelStyle}>
        {label || placeholder}
        {inputProps.isRequired && '*'}
      </Animated.Text>

      <Input
        {...restInputProps}
        pt={6}
        value={value}
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
      />

      {children}
    </Box>
  );
}
